import type { Protection } from '../collectors/privacy-signals';
import type { BrowserInfo } from '../content/tips';
import { explain } from '../content/explanations';
import { ESTIMATE_METHOD_VERSION } from '../entropy/estimates';
import { SHORT_HASH_LENGTH } from '../entropy/hash';
import { formatOneIn, topGroups, type GroupScore } from '../entropy/score';
import type { RunResult } from '../run';

export const APP_NAME = 'Fingerprint Self-Check';
export const APP_HOST = 'fingerprint.kathmannlabs.com';
export const APP_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';

export interface ExportReport {
  app: string;
  appVersion: string;
  estimateMethodVersion: string;
  generatedAt: string;
  note: string;
  summary: {
    band: string;
    estimatedBits: number;
    oneIn: string;
    browserGuess: string;
    topContributors: string[];
    protections: Array<Pick<Protection, 'id' | 'label' | 'strength'>>;
    changedBetweenRuns: string[];
  };
  stableHash: {
    algorithm: 'SHA-256';
    canonicalization: string;
    hex: string;
    short: string;
    input: Record<string, unknown>;
  };
  groups: GroupScore[];
  categories: Array<{
    id: string;
    title: string;
    status: string;
    signals: RunResult['signals'];
  }>;
}

export function contributorPhrases(result: RunResult): string[] {
  return topGroups(result.score).map((g) => explain(g.topSignalId ?? '').phrase ?? g.label.toLowerCase());
}

export function buildReport(result: RunResult, browser: BrowserInfo): ExportReport {
  return {
    app: APP_NAME,
    appVersion: APP_VERSION,
    estimateMethodVersion: ESTIMATE_METHOD_VERSION,
    generatedAt: result.startedAt,
    note: 'Generated entirely in your browser. This file leaves your device only if you share it. Bits are rough estimates, not measurements.',
    summary: {
      band: result.score.band,
      estimatedBits: result.score.totalBits,
      oneIn: formatOneIn(result.score.oneIn),
      browserGuess: browser.name,
      topContributors: contributorPhrases(result),
      protections: result.protections.map(({ id, label, strength }) => ({ id, label, strength })),
      changedBetweenRuns: result.changedBetweenRuns,
    },
    stableHash: {
      algorithm: 'SHA-256',
      canonicalization: 'UTF-8 JSON of `input` with object keys sorted recursively and no whitespace',
      hex: result.stable.hex,
      short: result.stable.hex.slice(0, SHORT_HASH_LENGTH),
      input: result.stable.input,
    },
    groups: result.score.groups,
    categories: result.categories.map((c) => ({ id: c.id, title: c.title, status: c.status, signals: c.signals })),
  };
}

export function reportToText(report: ExportReport): string {
  const lines: string[] = [
    `${report.app} v${report.appVersion} · estimate method v${report.estimateMethodVersion}`,
    `Generated ${report.generatedAt}`,
    '',
    `Exposure: ${report.summary.band.toUpperCase()}`,
    `Estimated identifying bits: ${report.summary.estimatedBits} (estimate, ≈ ${report.summary.oneIn})`,
    `Stable fingerprint hash: ${report.stableHash.short} (SHA-256 ${report.stableHash.hex})`,
    `Browser: ${report.summary.browserGuess}`,
    `Top contributors: ${report.summary.topContributors.join(', ') || 'none'}`,
    `Protections: ${report.summary.protections.map((p) => p.label).join('; ') || 'none detected'}`,
    `Changed between samples: ${report.summary.changedBetweenRuns.join(', ') || 'none'}`,
  ];
  for (const cat of report.categories) {
    lines.push('', `## ${cat.title}${cat.status === 'ok' ? '' : ` (${cat.status})`}`);
    for (const s of cat.signals) {
      const bits = s.bits === null ? 'not scored' : `${s.bits} bits`;
      const flags = [s.randomized ? 'randomized' : '', s.available ? '' : 'unavailable'].filter(Boolean).join(', ');
      const value = s.display.includes('\n') ? `\n    ${s.display.split('\n').join('\n    ')}` : ` ${s.display}`;
      lines.push(`- ${s.label} [${bits}${flags ? `; ${flags}` : ''}]:${value}`);
      if (s.note) lines.push(`    note: ${s.note}`);
    }
  }
  lines.push('', report.note);
  return lines.join('\n');
}

/** Save the report as a JSON file. Uses a blob: URL, so nothing goes over the network. */
export function downloadJson(report: ExportReport) {
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fingerprint-report-${report.stableHash.short}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}
